// Shared Meetings constants, helpers, seed data and Telegram payload normalizer.
// Used by MeetingsTab, MeetingsContext (shared store) and DesignFeeTab so the
// Meetings tab and the Design Fee tab stay in sync from a single source of truth.

export const LOCATION_OPTIONS = [
  { value: 'on_site', label: 'On site' },
  { value: 'office', label: 'Office' },
  { value: 'selection', label: 'Selection' },
  { value: 'other', label: 'Other' },
];
export const AGENDA_OPTIONS = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'design_review', label: 'Design Review' },
  { value: 'site_execution', label: 'Site Execution' },
  { value: 'selection', label: 'Selection' },
  { value: 'other', label: 'Other' },
];
export const LOCATION_LABEL = Object.fromEntries(LOCATION_OPTIONS.map(o => [o.value, o.label]));
export const AGENDA_LABEL = Object.fromEntries(AGENDA_OPTIONS.map(o => [o.value, o.label]));

export const genId = () => (crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));

export function formatDateDMY(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export function locationText(m) {
  if ((m.location === 'selection' || m.location === 'other') && m.locationCustom) return m.locationCustom;
  return LOCATION_LABEL[m.location] || '—';
}
export function agendaText(m) {
  if ((m.agenda === 'selection' || m.agenda === 'other') && m.agendaCustom) return m.agendaCustom;
  return AGENDA_LABEL[m.agenda] || '—';
}

// Clean WhatsApp/Telegram-ready MOM summary for sharing
export function buildMomSummary(m, projectName) {
  const lines = [`*Meeting #${m.number} — Minutes of Meeting*`];
  if (projectName) lines.push(`Project: ${projectName}`);
  lines.push(`Date: ${formatDateDMY(m.date)}`);
  lines.push(`Location: ${locationText(m)}`);
  lines.push(`Agenda: ${agendaText(m)}`);
  const attendees = (m.attendees || '').split(',').map(s => s.trim()).filter(Boolean);
  if (attendees.length) lines.push(`Attendees: ${attendees.join(', ')}`);
  const decisions = (m.decisions || []).filter(Boolean);
  if (decisions.length) {
    lines.push('', '*Decisions:*');
    decisions.forEach((d, i) => lines.push(`${i + 1}. ${d}`));
  }
  const actions = (m.actions || []).filter(Boolean);
  if (actions.length) {
    lines.push('', '*Action Items:*');
    actions.forEach(a => lines.push(`• ${a}`));
  }
  return lines.join('\n');
}

// Seed mock meetings (so list isn't empty on load)
export const SEED_MEETINGS = [
  {
    id: genId(), number: 1, date: '2026-06-14', location: 'on_site', locationCustom: '',
    agenda: 'site_execution', agendaCustom: '', attendees: 'Juthika, Site Engineer, Client',
    decisions: ['Finalize false ceiling layout for living room', 'Approve teak veneer for TV unit'],
    actions: ['Share revised ceiling drawing by Fri', 'Order veneer samples'], source: 'manual',
  },
  {
    id: genId(), number: 2, date: '2026-06-18', location: 'on_site', locationCustom: '',
    agenda: 'consultation', agendaCustom: '', attendees: 'Client, Lead Designer',
    decisions: ['Master bedroom wardrobe finalized in matte laminate'],
    actions: ['Update BOQ with wardrobe spec'], source: 'manual',
  },
  {
    id: genId(), number: 3, date: '2026-06-22', location: 'on_site', locationCustom: '',
    agenda: 'design_review', agendaCustom: '', attendees: 'Client, Procurement',
    decisions: ['Selected matte grey 600x600 for all bathrooms'],
    actions: ['Confirm stock & lead time with vendor'], source: 'telegram',
  },
];

// Telegram payload normalizer — incoming bot payload -> meeting model
export function normalizeTelegramPayload(payload) {
  return {
    id: payload.id || genId(),
    number: payload.number, // assigned by saveMeeting if missing
    date: payload.date || new Date().toISOString().slice(0, 10),
    location: payload.location || 'on_site',
    locationCustom: payload.locationCustom || '',
    agenda: payload.agenda || 'site_execution',
    agendaCustom: payload.agendaCustom || '',
    attendees: payload.attendees || '',
    decisions: Array.isArray(payload.decisions) ? payload.decisions : [],
    actions: Array.isArray(payload.actions) ? payload.actions : [],
    source: 'telegram',
  };
}
